import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DraggableModal from 'components/organisms/draggableModal';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import EmergencyModal from 'components/molecules/rpm-alert-assessment/emergencyModal';
import FormItemWrapper from 'components/molecules/rpm-alert-assessment/formItemWrapper';

import { assessmentSpo2Form } from '../templates/spo2';
import s from '../styles.module.scss';
import { criticalSpo2ValidationSchema } from '../validation-schema';
import { useSpoAssessment } from '../hooks/use-spo-assessment';
import { checkSymptomsUpdate, scrollToErrorElement } from '../helpers';
import { useStoreModalValue } from '../hooks/use-store-modal-value';
import { nursingInterventionOptions } from '../templates/common';

import type { ICriticalSpo2Props } from '../types';
import type { FC } from 'react';

const CriticalSpo2Modal: FC<ICriticalSpo2Props> = ({
  isOpen,
  activityId,
  type,
  activityDate,
  values,
  idx,
  patientGuid,
  handleSubmit,
  closeModal,
}) => {
  const {
    doctor: { addComment },
  } = useDispatch();

  const { t } = useTranslation();

  const {
    initialValues,
    openEmergencyModal,
    symptomsList,
    toggleEmergencyModal,
    onSaveForm,
  } = useSpoAssessment({
    type,
    activityId,
    addComment,
    activityDate,
    values,
    patientGuid,
    closeModal,
    handleSubmit,
  });

  type TInitialValues = typeof initialValues;
  const { parsedData, setModalValuesToStorage } =
    useStoreModalValue<TInitialValues>({
      idx,
      type,
      modalName: 'spo2',
    });
  const blockRef = useRef<HTMLDivElement | null>(null);
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const formik = useFormik({
    initialValues: parsedData ?? initialValues,
    onSubmit: (data) => {
      if (data.isOneMoreReading === 'false') {
        data.bounds = null;
        data.repeatReadingWas = null;
      }
      if (data.isTakeMedications === 'false') {
        data.prescribed = [];
        data.otherPrescribed = null;
      }
      if (data.isTakePrnMedications === 'false') {
        data.prnMedications = [];
        data.otherPrnMedications = null;
      }
      if (data.patient !== null) {
        data.other = null;
        data.relationship = null;
      }
      const isEscalation = data.nursingIntervention?.includes(
        nursingInterventionOptions[1].label,
      );
      if (!isEscalation) {
        data.escalationToClinic = [];
      }

      setModalValuesToStorage(data);

      onSaveForm(data);
    },
    validateOnChange: false,
    validationSchema: criticalSpo2ValidationSchema(symptomsList),
  });

  const requiredSymptomFields = symptomsList
    .filter((symptom) => symptom.required)
    .map((symptom) => symptom.value);

  const onSave = async () => {
    await formik.submitForm();
    scrollToErrorElement(formWrapperRef.current);
  };

  const onEmergencyModalCancel = () => {
    formik.setFieldValue('emergencyCare', false);
    toggleEmergencyModal();
  };

  const onEmergencyModalSave = () => {
    formik.setFieldValue('emergencyCare', true);
    toggleEmergencyModal();
    if (blockRef.current) {
      blockRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (formik.values.checkSymptoms) {
      checkSymptomsUpdate(requiredSymptomFields, formik, toggleEmergencyModal);
    }
  }, [formik.values]);

  const expandableExtraFields = [
    'isTakeMedications',
    'isTakePrnMedications',
    'isOneMoreReading',
  ];

  return (
    <>
      <DraggableModal
        isOpen={isOpen}
        toggle={closeModal}
        title={t('labels.rpmAlertBasedAssessment', {
          name: t('labels.hypoxiaAssessment'),
        })}
        onSave={onSave}
      >
        <BaseForm formik={formik}>
          <div className={s.wrapper} ref={formWrapperRef}>
            {assessmentSpo2Form.map((formItem, index) => {
              const isShowExtraFields = expandableExtraFields.includes(
                formItem.name,
              )
                ? formik.getFieldMeta(formItem.name)?.value === 'true'
                : undefined;

              return (
                <FormItemWrapper
                  formik={formik}
                  index={index}
                  formItem={formItem}
                  type={type}
                  requiredSymptomFields={requiredSymptomFields}
                  ref={blockRef}
                  isShowExtraFields={isShowExtraFields}
                />
              );
            })}
          </div>
        </BaseForm>
      </DraggableModal>

      {openEmergencyModal && (
        <EmergencyModal
          openEmergencyModal={openEmergencyModal}
          onSave={onEmergencyModalSave}
          onCancel={onEmergencyModalCancel}
        />
      )}
    </>
  );
};

export default CriticalSpo2Modal;
